import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { usersApi } from '@/lib/api'
import { sendInvites, type InviteFailure } from '@/lib/inviteBatch'
import { useNotificationStore } from './notifications'

export interface User {
  _id: string
  name: string
  email: string
  role: UserRole
  status: UserStatus
  active?: boolean
  emailVerified?: boolean
  orgId: string
  lastLoginAt?: string
  avatar?: string
  createdAt: string
  updatedAt: string
}

export enum UserRole {
  OWNER = 'owner',
  SUPER_ADMIN = 'super_admin',
  ADMIN = 'admin',
  KAM = 'kam',
  MEMBER = 'member',
  READONLY = 'readonly'
}

export enum UserStatus {
  ACTIVE = 'active',
  DISABLED = 'disabled',
  INVITED = 'invited'
}

export interface CreateUserData {
  email: string
  name: string
  password: string
  role: UserRole
  status?: UserStatus
}

export interface UsersState {
  users: User[]
  loading: boolean
  error: string | null
  meta: {
    page: number
    pageSize: number
    total: number
    hasNext: boolean
  } | null
}

export const useUsersStore = defineStore('users', () => {
  const users = ref<User[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref<UsersState['meta']>(null)
  
  const notifications = useNotificationStore()

  // Computed
  const usersCount = computed(() => users.value.length)

  const activeUsers = computed(() => 
    users.value.filter(user => user.status === UserStatus.ACTIVE || user.active)
  )

  const usersByRole = computed(() => {
    const grouped: Record<string, User[]> = {}
    users.value.forEach(user => {
      if (!grouped[user.role]) {
        grouped[user.role] = []
      }
      grouped[user.role]!.push(user)
    })
    return grouped
  })

  const adminUsers = computed(() => 
    users.value.filter(user => user.role === UserRole.ADMIN || user.role === UserRole.OWNER)
  )

  // Actions
  async function fetchUsers(params?: any): Promise<void> {
    loading.value = true
    error.value = null

    // The users list is used everywhere as an owner-lookup index (avatars,
    // names, filter dropdowns, "Unknown User" fallback). The API default
    // pageSize is 10 — that silently chopped any team past the first page off
    // the lookup, surfacing as "Unknown User" on every company / deal owned
    // by a later user. Default to 200 here unless an explicit pageSize is
    // passed. Audit ref X-03.
    const effectiveParams = { pageSize: 200, ...(params || {}) }

    try {
      const response = await usersApi.list(effectiveParams)
      
      // Handle both possible response formats
      if (response.data && Array.isArray(response.data)) {
        users.value = response.data
        meta.value = response.meta || null
      } else if (Array.isArray(response)) {
        // Direct array response
        users.value = response
        meta.value = null
      } else {
        console.error('Unexpected response format:', response)
        users.value = []
        meta.value = null
      }
    } catch (err: any) {
      console.error('Error fetching users:', err)
      error.value = err.response?.data?.message || err.message || 'Failed to fetch users'
      notifications.error('Failed to load users')
      users.value = []
    } finally {
      loading.value = false
    }
  }

  async function fetchUser(id: string): Promise<User | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await usersApi.get(id)
      const user = response.data
      
      // Update user in store if it exists
      const index = users.value.findIndex(u => u._id === id)
      if (index !== -1) {
        users.value[index] = user
      } else {
        users.value.push(user)
      }
      
      return user
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch user'
      notifications.error('Failed to load user')
      return null
    } finally {
      loading.value = false
    }
  }

  async function createUser(userData: CreateUserData): Promise<User | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await usersApi.create(userData)
      // The users endpoints return the entity directly (not wrapped in { data }),
      // unlike the list endpoints. Accept either shape so the modal reliably
      // closes — including on a cross-org add, where an existing user is returned.
      const newUser = (response as any)?.data ?? response

      // Add the new user to the list (avoid duplicates: a cross-org add returns an
      // existing account that may already be in the list).
      if (newUser?._id) {
        users.value = [...users.value.filter(u => u._id !== newUser._id), newUser]
      }

      notifications.success('User created successfully')
      return newUser
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to create user'
      notifications.error(error.value!)
      return null
    } finally {
      loading.value = false
    }
  }

  async function updateUser(id: string, userData: Partial<User>): Promise<User | null> {
    loading.value = true
    error.value = null
    
    try {
      const response = await usersApi.update(id, userData)
      // Users endpoints return the entity directly (not wrapped in { data }).
      const updatedUser = (response as any)?.data ?? response

      // Update user in the list using immutable update
      if (updatedUser?._id) {
        const index = users.value.findIndex(u => u._id === id)
        if (index !== -1) {
          users.value = [
            ...users.value.slice(0, index),
            updatedUser,
            ...users.value.slice(index + 1)
          ]
        }
      }
      
      notifications.success('User updated successfully')
      return updatedUser
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to update user'
      notifications.error(error.value!)
      return null
    } finally {
      loading.value = false
    }
  }

  async function deleteUser(id: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      await usersApi.delete(id)
      // Remove user using immutable update
      users.value = users.value.filter(u => u._id !== id)
      notifications.success('User deleted successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to delete user'
      notifications.error(error.value!)
      return false
    } finally {
      loading.value = false
    }
  }

  async function inviteUser(email: string, role: User['role']): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      await usersApi.invite({ email, role })
      notifications.success('User invitation sent successfully')
      // Refresh users list to include the invited user
      await fetchUsers()
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to invite user'
      notifications.error('Failed to send user invitation')
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * Invites several people with one role (and an optional personal note for the email): one
   * `POST /users/invite` per address, one after another. A refused address (already a member, a
   * typo the API rejects) does not stop the rest; it comes back in `failed` with the API's reason.
   * No toasts and no list refresh: the Invite people dialog says what happened and emits `invited`,
   * on which its host refreshes what it shows. Rejects only with the demo org's write block (its
   * toast was already shown).
   */
  async function inviteUsers(
    emails: string[],
    role: string,
    note?: string,
  ): Promise<{ invited: User[]; failed: InviteFailure[] }> {
    return sendInvites(emails, async (email) => {
      const res: any = await usersApi.invite({ email, role, ...(note ? { note } : {}) })
      // The invite endpoint answers with the user itself; accept a `{ data }` wrapper too.
      return (res?.data?._id ? res.data : res) as User
    })
  }

  async function resendInvitation(id: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      await usersApi.resendInvitation(id)
      notifications.success('Invitation resent successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to resend invitation'
      notifications.error('Failed to resend invitation')
      return false
    } finally {
      loading.value = false
    }
  }

  async function deactivateUser(id: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await usersApi.deactivate(id)
      const updatedUser = response.data
      
      // Update user in the list using immutable update
      if (updatedUser) {
        const index = users.value.findIndex(u => u._id === id)
        if (index !== -1) {
          users.value = [
            ...users.value.slice(0, index),
            updatedUser,
            ...users.value.slice(index + 1)
          ]
        }
      }
      
      notifications.success('User deactivated successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to deactivate user'
      notifications.error(error.value!)
      return false
    } finally {
      loading.value = false
    }
  }

  async function activateUser(id: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      const response = await usersApi.activate(id)
      const updatedUser = response.data
      
      // Update user in the list using immutable update
      if (updatedUser) {
        const index = users.value.findIndex(u => u._id === id)
        if (index !== -1) {
          users.value = [
            ...users.value.slice(0, index),
            updatedUser,
            ...users.value.slice(index + 1)
          ]
        }
      }
      
      notifications.success('User activated successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to activate user'
      notifications.error(error.value!)
      return false
    } finally {
      loading.value = false
    }
  }

  function getUserById(id: string): User | undefined {
    return users.value.find(u => u._id === id)
  }

  function getUserFullName(user: User): string {
    return user.name || user.email
  }

  function getUserInitials(user: User): string {
    const name = getUserFullName(user)
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  function getUserRoleColor(role: string): string {
    switch (role) {
      case 'super_admin':
        return 'bg-ink text-white'
      case 'owner':
        return 'bg-ink text-white'
      case 'admin':
        return 'bg-accent-tint text-accent-hover'
      case 'kam':
        return 'bg-info-tint text-info-text'
      case 'member':
        return 'bg-wash text-ink-2'
      case 'readonly':
        return 'bg-wash text-ink-3'
      default:
        return 'bg-wash text-ink-2'
    }
  }

  function clearUsers(): void {
    users.value = []
    meta.value = null
    error.value = null
  }

  return {
    // State
    users,
    loading,
    error,
    meta,
    
    // Computed
    usersCount,
    activeUsers,
    usersByRole,
    adminUsers,
    
    // Actions
    fetchUsers,
    fetchUser,
    createUser,
    updateUser,
    deleteUser,
    inviteUser,
    inviteUsers,
    resendInvitation,
    deactivateUser,
    activateUser,
    getUserById,
    getUserFullName,
    getUserInitials,
    getUserRoleColor,
    clearUsers,
  }
})